import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "../../lib/api";
import { formatDuration } from "../../lib/format";
import { normalizeImageUrl } from "../../lib/imageUrl";
import PublicNav from "../../components/PublicNav";
import { ShieldCheck, Lock, ArrowLeft, UsersThree, Gift } from "@phosphor-icons/react";

export default function Checkout() {
  const { instanceId } = useParams();
  const [params] = useSearchParams();
  const waitlistToken = params.get("waitlist_token");
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [giftCode, setGiftCode] = useState("");
  const [giftCheck, setGiftCheck] = useState(null);
  const [checkingGift, setCheckingGift] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    guest_count: 0, dietary_notes: "", waiver_accepted: false, marketing_opt_in: false, terms_accepted: false,
    payment_mode: "full",
    co_guests: [],
  });

  const fetchQuote = (gc) => api.get(`/quote?instance_id=${instanceId}&guest_count=${gc}`).then(r => setQuote(r.data));

  useEffect(() => {
    api.get(`/quote?instance_id=${instanceId}&guest_count=1`).then(r => {
      setQuote(r.data);
      const tour = r.data.tour;
      const minG = Math.max(1, tour.min_guests || 1);
      const initialMode = tour.split_pay_enabled && minG > 1 ? "split" : "full";
      const co = initialMode === "split" ? Array.from({length: minG - 1}, () => ({ name: "", email: "", phone: "" })) : [];
      setForm(f => ({ ...f, guest_count: minG, payment_mode: initialMode, co_guests: co }));
      // refresh quote with proper guest_count
      fetchQuote(minG).catch(() => {});
    }).catch(() => {});
    /* eslint-disable-next-line */
  }, [instanceId]);

  const updateGuests = (n) => {
    setForm(f => {
      const need = Math.max(0, n - 1);
      const co = (f.co_guests || []).slice(0, need);
      while (co.length < need) co.push({ name: "", email: "", phone: "" });
      return { ...f, guest_count: n, co_guests: co };
    });
    fetchQuote(n).catch(() => {});
  };

  const updateCoGuest = (i, field, val) => {
    setForm(f => ({ ...f, co_guests: f.co_guests.map((c, idx) => idx === i ? { ...c, [field]: val } : c) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.waiver_accepted) { setError("Please accept the waiver."); return; }
    if (form.payment_mode === "split") {
      const need = form.guest_count - 1;
      const co = form.co_guests.slice(0, need);
      if (co.some(c => !c.name)) { setError("Please add a name for every guest."); return; }
      if (co.some(c => !((c.email || "").trim() || (c.phone || "").trim()))) {
        setError("Each guest needs an email or a phone number so we can send their payment link.");
        return;
      }
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        co_guests: form.payment_mode === "split" ? form.co_guests.slice(0, form.guest_count - 1) : [],
        instance_id: instanceId,
        origin_url: window.location.origin,
      };
      const qs = new URLSearchParams();
      if (giftCheck?.valid) qs.set("gift_card_code", giftCheck.code);
      if (waitlistToken) qs.set("waitlist_token", waitlistToken);
      const url = qs.toString() ? `/bookings/checkout?${qs.toString()}` : "/bookings/checkout";
      const res = await api.post(url, payload);
      if (res.data.skipped_stripe) {
        // Gift card covered the full lead share — go straight to success
        window.location.href = `/booking/success?booking_id=${res.data.booking_id}`;
      } else {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  const checkGiftCode = async () => {
    if (!giftCode.trim()) return;
    setCheckingGift(true);
    setGiftCheck(null);
    try {
      const r = await api.get(`/gift-cards/validate/${encodeURIComponent(giftCode.trim().toUpperCase())}`);
      setGiftCheck(r.data);
    } catch (e) {
      setGiftCheck({ valid: false, reason: "Could not check this code" });
    } finally {
      setCheckingGift(false);
    }
  };

  if (!quote || form.guest_count === 0) return <div className="public-shell"><PublicNav /><p className="pt-40 text-center text-ms-stone">Loading…</p></div>;
  const { tour, instance, total, available, sold_out } = quote;
  const dt = new Date(instance.starts_at);
  const minG = Math.max(1, tour.min_guests || 1);
  const maxG = Math.min(available, tour.max_guests || 12);
  const splitAllowed = !!tour.split_pay_enabled && form.guest_count > 1;
  const splitMode = splitAllowed && form.payment_mode === "split";
  const sharePerGuest = form.guest_count > 0 ? Math.round((total / form.guest_count) * 100) / 100 : 0;
  const grossDue = splitMode ? sharePerGuest : total;
  const giftApplied = giftCheck?.valid ? Math.min(giftCheck.balance_remaining, grossDue) : 0;
  const dueNow = Math.max(0, grossDue - giftApplied);

  return (
    <div className="public-shell">
      <PublicNav />
      <section className="bg-ms-cream pt-32 pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <Link to={`/tour/${tour.slug}`} className="inline-flex items-center gap-2 text-ms-stone hover:text-ms-ink text-sm">
              <ArrowLeft size={14} /> Back to tour
            </Link>
            <p className="label-eyebrow text-ms-brass mt-6">Reservation</p>
            <h1 className="serif text-ms-ink text-4xl md:text-5xl mt-3 leading-[1.05]">Confirm your party.</h1>

            {sold_out && <p className="mt-6 text-ms-danger">This date is sold out.</p>}

            <form onSubmit={submit} className="mt-10 space-y-7" data-testid="checkout-form">
              {/* Step 1 — Lead booker */}
              <Section title="Your details">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Full name" required>
                    <input value={form.customer_name} onChange={e=>setForm({...form, customer_name:e.target.value})} required className="field-input" data-testid="checkout-name" />
                  </Field>
                  <Field label="Email" required>
                    <input type="email" value={form.customer_email} onChange={e=>setForm({...form, customer_email:e.target.value})} required className="field-input" data-testid="checkout-email" />
                  </Field>
                  <Field label="Phone" required>
                    <input value={form.customer_phone} onChange={e=>setForm({...form, customer_phone:e.target.value})} required className="field-input" data-testid="checkout-phone" />
                    <p className="mt-2 text-[11px] leading-[1.55] text-ms-stone" data-testid="checkout-sms-consent">
                      By providing your phone number you agree to receive booking confirmations, tour reminders, and day-of instructions via SMS from Mystery Served. Message and data rates may apply. Reply STOP to opt out, HELP for help. See our <a href="/privacy" target="_blank" rel="noreferrer" className="text-ms-brass hover:underline">Privacy Policy</a>.
                    </p>
                  </Field>
                  <Field label={`Guests (${minG}–${maxG})`} required>
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={()=>updateGuests(Math.max(minG, form.guest_count-1))} disabled={form.guest_count <= minG} className="w-10 h-10 border border-ms-edge bg-white rounded-full text-ms-ink hover:bg-ms-cream disabled:opacity-40" data-testid="checkout-guests-minus">−</button>
                      <span className="serif text-3xl text-ms-ink w-12 text-center" data-testid="checkout-guests-count">{form.guest_count}</span>
                      <button type="button" onClick={()=>updateGuests(Math.min(maxG, form.guest_count+1))} disabled={form.guest_count >= maxG} className="w-10 h-10 border border-ms-edge bg-white rounded-full text-ms-ink hover:bg-ms-cream disabled:opacity-40" data-testid="checkout-guests-plus">+</button>
                    </div>
                  </Field>
                </div>
              </Section>

              {/* Step 2 — Payment mode picker (split-pay tours only) */}
              {splitAllowed && (
                <Section title="How will you pay?">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup">
                    <Option
                      active={!splitMode}
                      onClick={() => setForm({...form, payment_mode: "full"})}
                      title="Pay in full"
                      desc={`You pay $${total.toFixed(2)} now for the whole party.`}
                      testId="payment-mode-full"
                    />
                    <Option
                      active={splitMode}
                      onClick={() => setForm({...form, payment_mode: "split"})}
                      title="Split with my guests"
                      desc={`You pay $${sharePerGuest.toFixed(2)} now to lock the date. Each guest gets a private link to pay their share.`}
                      testId="payment-mode-split"
                    />
                  </div>
                </Section>
              )}

              {/* Step 3 — Co-guest list (only when split-pay) */}
              {splitMode && form.guest_count > 1 && (
                <Section title="Your guests" eyebrow={<><UsersThree size={14} weight="duotone" className="inline mr-1.5 text-ms-brass" />Give us an <b>email or a phone number</b> for each guest &mdash; we&apos;ll send their private payment link via whichever you fill in (or both). Their <b>${sharePerGuest.toFixed(2)}</b> share is charged separately.</>}>
                  <div className="space-y-4">
                    {form.co_guests.slice(0, form.guest_count - 1).map((cg, i) => {
                      const hasEither = !!((cg.email || "").trim() || (cg.phone || "").trim());
                      return (
                        <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1.1fr_1fr] gap-2" data-testid={`co-guest-row-${i}`}>
                          <input value={cg.name} onChange={e=>updateCoGuest(i, "name", e.target.value)} placeholder={`Guest ${i+2} name`} className="field-input" required data-testid={`co-guest-name-${i}`} />
                          <input type="email" value={cg.email} onChange={e=>updateCoGuest(i, "email", e.target.value)} placeholder="Email (or leave blank)" className="field-input" data-testid={`co-guest-email-${i}`} />
                          <input type="tel" value={cg.phone} onChange={e=>updateCoGuest(i, "phone", e.target.value)} placeholder="Phone (or leave blank)" className="field-input" data-testid={`co-guest-phone-${i}`} />
                          {cg.name && !hasEither && (
                            <p className="md:col-span-3 text-xs text-ms-danger -mt-1" data-testid={`co-guest-need-channel-${i}`}>
                              Add an email <b>or</b> a phone number so we can send their payment link.
                            </p>
                          )}
                        </div>
                      );
                    })}
                    <p className="text-ms-stone text-[11px] leading-[1.55]">Don&apos;t know your friend&apos;s email? No worries &mdash; just put their cell number. We&apos;ll text the link.</p>
                  </div>
                </Section>
              )}

              {/* Step 4 — Lead's waiver + dietary (full-pay collects whole-party dietary; split-pay collects only lead's) */}
              <Section title={splitMode ? "Your share & waiver" : "Final details"}>
                <Field label={splitMode ? "Your dietary restrictions / allergies" : "Dietary restrictions / allergies (whole party)"}>
                  <textarea rows={3} value={form.dietary_notes} onChange={e=>setForm({...form, dietary_notes:e.target.value})} className="field-input" data-testid="checkout-dietary" placeholder="Vegetarian, gluten-free, allergies…" />
                </Field>

                <Field label="Special occasion? (optional)">
                  <input value={form.special_occasion} onChange={e=>setForm({...form, special_occasion:e.target.value})} className="field-input" data-testid="checkout-special-occasion" placeholder="Birthday, anniversary, first date, bachelorette…" />
                </Field>

                {/* Gift card / promo code */}
                <div className="mt-4 bg-ms-cream/40 border border-ms-edge rounded-md p-4" data-testid="checkout-gift-card">
                  <p className="label-eyebrow text-ms-stone text-[10px] flex items-center gap-1.5"><Gift size={12} weight="duotone" className="text-ms-brass" /> Have a Mystery Served code?</p>
                  <div className="mt-2 flex items-center gap-2">
                    <input
                      value={giftCode}
                      onChange={e => { setGiftCode(e.target.value.toUpperCase()); setGiftCheck(null); }}
                      placeholder="MS-XXXXXXXX"
                      className="flex-1 field-input font-mono"
                      data-testid="checkout-gift-input"
                    />
                    <button
                      type="button"
                      onClick={checkGiftCode}
                      disabled={!giftCode.trim() || checkingGift}
                      className="px-4 py-2 text-xs border border-ms-earth text-ms-earth rounded-md hover:bg-ms-earth hover:text-ms-cream disabled:opacity-40"
                      data-testid="checkout-gift-apply"
                    >
                      {checkingGift ? "Checking…" : "Apply"}
                    </button>
                  </div>
                  {giftCheck?.valid && (
                    <p className="mt-2 text-xs text-ms-success" data-testid="checkout-gift-valid">
                      ✓ ${giftCheck.balance_remaining.toFixed(2)} available · ${giftApplied.toFixed(2)} applied to this booking
                    </p>
                  )}
                  {giftCheck && !giftCheck.valid && (
                    <p className="mt-2 text-xs text-ms-danger" data-testid="checkout-gift-invalid">{giftCheck.reason}</p>
                  )}
                </div>

                <label className="flex items-start gap-3 cursor-pointer mt-4">
                  <input type="checkbox" checked={form.waiver_accepted} onChange={e=>setForm({...form, waiver_accepted:e.target.checked})} className="mt-1 accent-ms-brass" data-testid="checkout-waiver" />
                  <span className="text-ms-ink/85 text-sm leading-[1.6]">I acknowledge the experience requires walking, includes food but not drinks, and I accept the cancellation policy.{splitMode ? " (Each guest will sign their own waiver before paying.)" : ""}</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer mt-3">
                  <input type="checkbox" checked={form.marketing_opt_in} onChange={e=>setForm({...form, marketing_opt_in:e.target.checked})} className="mt-1 accent-ms-brass" data-testid="checkout-marketing" />
                  <span className="text-ms-stone text-sm">Send me word when new tours are scheduled.</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer mt-4 pt-4 border-t border-ms-edge/60">
                  <input type="checkbox" required checked={form.terms_accepted} onChange={e=>setForm({...form, terms_accepted:e.target.checked})} className="mt-1 accent-ms-brass" data-testid="checkout-terms" />
                  <span className="text-ms-ink/85 text-sm leading-[1.6]">
                    I agree to the{" "}
                    <a href="/terms" target="_blank" rel="noreferrer" className="text-ms-brass hover:underline" data-testid="checkout-terms-link">Terms of Service</a>
                    {" "}and{" "}
                    <a href="/privacy" target="_blank" rel="noreferrer" className="text-ms-brass hover:underline" data-testid="checkout-privacy-link">Privacy Policy</a>. <span className="text-ms-danger">*</span>
                  </span>
                </label>
              </Section>

              {error && (
                <div className="rounded-sm border border-ms-danger/40 bg-ms-danger/5 p-4" data-testid="checkout-error">
                  <p className="text-ms-danger text-sm font-medium">We couldn&apos;t start checkout.</p>
                  <p className="text-ms-danger text-xs mt-1.5 leading-[1.55]">{error}</p>
                  <p className="text-ms-stone text-xs mt-2">If this keeps happening, please email <a className="underline" href="mailto:info@mysteryserved.com">info@mysteryserved.com</a> and we&apos;ll get you booked manually.</p>
                </div>
              )}
              <button disabled={loading || sold_out || !form.terms_accepted || !form.waiver_accepted} type="submit" className="w-full py-4 bg-ms-ink text-ms-cream hover:bg-ms-brass rounded-full transition-all label-eyebrow text-xs disabled:opacity-50 disabled:cursor-not-allowed" data-testid="checkout-submit">
                {loading ? "Redirecting…" : (splitMode
                  ? `Pay your share — $${dueNow.toFixed(2)} & lock the date`
                  : `Pay $${dueNow.toFixed(2)} securely`)}
              </button>
              <p className="flex items-center justify-center gap-2 text-ms-stone text-xs"><Lock size={14} /> Secured by Stripe · You will be redirected.</p>
            </form>
          </div>

          <aside className="lg:col-span-5">
            <div className="bg-ms-ivory border border-ms-edge rounded-sm overflow-hidden sticky top-28 shadow-sm">
              {tour.hero_image && <img src={normalizeImageUrl(tour.hero_image)} alt="" className="w-full aspect-[3/2] object-cover" />}
              <div className="p-7">
                <p className="label-eyebrow text-ms-brass">Booking summary</p>
                <p className="serif text-3xl text-ms-ink mt-2">{tour.title}</p>
                <div className="mt-5 space-y-3 text-sm">
                  <Row k="When" v={dt.toLocaleString(undefined, {weekday:'long', month:'long', day:'numeric', hour:'numeric', minute:'2-digit'})} />
                  <Row k="Where" v={tour.meeting_location || "Shared after booking"} />
                  <Row k="Duration" v={formatDuration(tour)} />
                  <Row k="Guests" v={form.guest_count} />
                </div>
                <div className="brass-line my-5" />
                <Row k="Tour total" v={`$${total.toFixed(2)}`} />
                {splitMode && (
                  <>
                    <Row k="Per guest" v={`$${sharePerGuest.toFixed(2)}`} />
                    <Row k="Outstanding (after deposit)" v={`$${(total - sharePerGuest).toFixed(2)}`} />
                  </>
                )}
                {giftApplied > 0 && (
                  <div className="mt-2 flex items-start justify-between gap-4">
                    <span className="text-ms-brass">Gift card applied</span>
                    <span className="text-ms-brass text-right">−${giftApplied.toFixed(2)}</span>
                  </div>
                )}
                <div className="brass-line my-5" />
                <div className="flex items-center justify-between">
                  <span className="label-eyebrow text-ms-stone text-[10px]">{splitMode ? "Due now (your share)" : "Total"}</span>
                  <span className="serif text-3xl text-ms-ink" data-testid="checkout-total">${dueNow.toFixed(2)}</span>
                </div>
                <p className="mt-4 flex items-center gap-2 text-xs text-ms-stone"><ShieldCheck size={14} /> Inventory held until payment completes.</p>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow text-ms-stone text-[10px]">{label}{required && <span className="text-ms-brass"> *</span>}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
function Row({ k, v }) {
  return <div className="flex items-start justify-between gap-4"><span className="text-ms-stone">{k}</span><span className="text-ms-ink text-right">{v}</span></div>;
}
function Option({ active, onClick, title, desc, testId }) {
  return (
    <button type="button" onClick={onClick} role="radio" aria-checked={active} data-testid={testId}
      className={`text-left p-4 rounded-md border transition-all ${active ? "border-ms-brass bg-ms-cream" : "border-ms-edge bg-white hover:border-ms-stone"}`}>
      <p className="text-ms-ink text-sm font-medium">{title}</p>
      <p className="text-ms-stone text-xs mt-1.5 leading-[1.55]">{desc}</p>
    </button>
  );
}
function Section({ title, eyebrow, children }) {
  return (
    <div className="bg-ms-ivory border border-ms-edge rounded-sm p-6">
      <p className="serif text-xl text-ms-ink">{title}</p>
      {eyebrow && <p className="text-ms-stone text-xs mt-1.5 leading-[1.6]">{eyebrow}</p>}
      <div className="mt-5">{children}</div>
    </div>
  );
}
